/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { FolderType } from '../../types/FolderType';
import { FolderTree } from '../../components/folder/FolderTree';
import { FolderModal } from '../../components/folder/FolderModal';
import { Button } from '../../components/ui/Button';
import { FileText, Plus } from 'lucide-react';
import { FolderService } from '../../services/folder.service';

export const FoldersPage = () => {
  const [folders, setFolders] = useState<FolderType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<FolderType | null>(null);
  const [defaultParent, setDefaultParent] = useState<FolderType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadFolders = async () => {
    try {
      setIsLoading(true);
      const all = await FolderService.getAllWithHierarchy();
      setFolders(all);
    } catch (error) {
      console.error('Failed to load folders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFolders();
  }, []);

  const categorized = folders.filter((f) => f.id !== 'uncategorized');
  const uncategorized = folders.find((f) => f.id === 'uncategorized');

  return (
    <div>
      <h1 className="text-4xl font-bold text-center sm:pb-4 sm:text-left text-primary">Folders</h1>

      <div className="flex justify-center sm:justify-start">
        <Button
          icon={<Plus />}
          className="px-16 py-3 mt-4 sm:px-6 sm:mt-0"
          onClick={() => {
            setIsModalOpen(true);
            setDefaultParent(null);
            setEditingFolder(null);
          }}
        >
          New
        </Button>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary">Loading...</p>
        ) : folders.length === 0 ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary">No folders found.</p>
        ) : (
          <div className="space-y-2">
            {/* Categorized folders */}
            {categorized.map((folder) => (
              <FolderTree
                key={folder.id}
                folder={folder}
                onEdit={(f) => {
                  setEditingFolder(f);
                  setIsModalOpen(true);
                }}
                onDelete={async (f) => {
                  await FolderService.delete(f.id);
                  loadFolders();
                }}
                onAdd={(f) => {
                  setDefaultParent(f);
                  setEditingFolder(null);
                  setIsModalOpen(true);
                }}
              />
            ))}

            {uncategorized?.files && uncategorized.files.length > 0 && (
              <FolderTree
                folder={{
                  id: 'uncategorized-virtual',
                  folderName: 'Uncategorized',
                  files: uncategorized.files,
                  subfolders: [],
                  parent: null,
                  createdAt: new Date().toISOString(), // dummy timestamp
                  isArchived: false,
                  isActive: true,
                }}
                onEdit={() => {}}
                onDelete={() => {}}
                onAdd={() => {}}
              />
            )}
          </div>
        )}
      </div>

      <FolderModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingFolder(null);
          setDefaultParent(null);
          loadFolders();
        }}
        onSuccess={loadFolders}
        editingFolder={editingFolder}
        defaultParent={defaultParent}
      />
    </div>
  );
};
